import React, { useCallback, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  Button,
  formatSnakeCaseToCamelCase,
  HighlightedWrapper,
  Hint,
  Inscription,
  List,
  NoUsersIcon,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { PendingModal } from '@/components';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';

import AddBeneficiaryModal from './AddBeneficiaryModal';
import { useHighlightedBeneficiaryStore } from './stores';
import useDeleteBeneficiariesQuery from './useDeleteBeneficiaries.query';
import { Beneficiary, BeneficiaryType } from './useGetBeneficiaries.query';

const HINT_TEXT = 'Terminated Organization cannot add beneficiaries';

interface StyledButtonProps {
  isWarning?: boolean;
}

const StyledWrapper = styled(Box)`
  > div:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border2};
    padding-bottom: 12px;
    margin-bottom: 12px;
  }
`;

const StyledButton = styled(Button)<StyledButtonProps>`
  height: 21px !important;
  min-height: 21px !important;
  ${({ theme }) => theme.fonts.text14Regular};
  font-weight: 400 !important;
  margin-bottom: ${({ theme }) => theme.spacings.s};
  color: ${({ theme, isWarning }) => (isWarning ? `${theme.colors.danger} !important` : '')};
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    color: ${({ theme, isWarning }) => (isWarning ? theme.colors.danger : theme.colors.textAccent)};
  }
`;

interface Props {
  beneficiaries?: Beneficiary[];
  beneficiaryType?: BeneficiaryType;
  isRemovingAllPrimaryBeneficiariesButtonHidden?: boolean;
  isAddButtonDisabled?: boolean;
}

const BeneficiariesList: React.FC<Props> = ({
  beneficiaries,
  beneficiaryType,
  isAddButtonDisabled,
  isRemovingAllPrimaryBeneficiariesButtonHidden,
}) => {
  const { isMobile } = useResponsive();
  const typeTitle = useMemo(() => capitalize(beneficiaryType), [beneficiaryType]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isRemovingModalShown, setIsRemovingModalShown] = useState(false);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { isDisabledDueToTermination } = useEmployerTerminationBanner();
  const onSuccess = useCallback(() => {
    setIsRemovingModalShown(false);
    setIsRemoving(false);
    handleAddPermanentSnackbar({
      text: `${typeTitle} beneficiaries has been removed`,
      state: SnackbarState.positive,
      autoRemove: true,
      closeIcon: true,
    });
  }, [setIsRemovingModalShown, setIsRemoving, handleAddPermanentSnackbar, typeTitle]);
  const { deleteBeneficiaries } = useDeleteBeneficiariesQuery({
    beneficiaryType,
    onSuccess,
  });
  const highlightedIds = useHighlightedBeneficiaryStore((state) => state.highlightedIds);
  return (
    <Box
      data-testId={`BeneficiariesSection_${beneficiaryType}`}
      round="moduleRound"
      pad={isMobile ? 'spacing16' : 'spacing24'}
      background="module"
    >
      <Box
        height={isMobile ? undefined : '40px'}
        align={isMobile ? undefined : 'center'}
        justify="between"
        margin={{ bottom: 'spacing16' }}
        direction={isMobile ? 'column' : 'row'}
        gap={isMobile ? 'spacing12' : undefined}
      >
        <Box direction="row" align="center" justify="between" width={isMobile ? '100%' : undefined}>
          <Inscription color="textTitle" weight="bold" lineHeight="22px" size="large">
            {typeTitle} Beneficiaries
          </Inscription>
        </Box>
        {beneficiaries?.length ? null : (
          <Hint
            hintAlign="left"
            hintWidth={220}
            hideHint={!isDisabledDueToTermination}
            {...(isMobile ? { wrapperWidth: '100%', width: '100%' } : {})}
            hintElement={
              <AppButton
                testId={`BeneficiariesList_add_${beneficiaryType}`}
                disabled={isAddButtonDisabled || isDisabledDueToTermination}
                buttonType="secondary"
                width={isMobile ? '100%' : undefined}
                onClick={() => setIsModalShown(true)}
              >
                Add {typeTitle} Beneficiary
              </AppButton>
            }
            marginContent="m"
          >
            <Box direction="row" justify="center" align="center">
              <Inscription textAlign="center">{HINT_TEXT}</Inscription>
            </Box>
          </Hint>
        )}
        {isModalShown ? (
          <AddBeneficiaryModal
            beneficiaryType={beneficiaryType}
            onClose={() => setIsModalShown(false)}
            beneficiaries={beneficiaries}
          />
        ) : null}
        {isRemovingModalShown ? (
          <PendingModal
            testId="remove_beneficiary"
            visible
            onSetVisible={() => setIsRemovingModalShown(false)}
            header={`Remove ${typeTitle} Beneficiaries?`}
          >
            <Box fill={isMobile} direction={isMobile ? 'column-reverse' : 'row'} gap="spacing12">
              <AppButton
                testId="cancel_removing_beneficiary"
                onClick={() => setIsRemovingModalShown(false)}
                width={isMobile ? undefined : '140px'}
                fill={isMobile}
                buttonType="secondary"
              >
                Cancel
              </AppButton>

              <AppButton
                testId="remove_beneficiary"
                color="red"
                onClick={() => {
                  deleteBeneficiaries();
                  setIsRemoving(true);
                }}
                fill={isMobile}
                width={isMobile ? undefined : '140px'}
                disabled={isRemoving}
              >
                {isRemoving ? <Preloader /> : 'Remove'}
              </AppButton>
            </Box>
          </PendingModal>
        ) : null}
      </Box>

      {!isMobile && (
        <HighlightedWrapper
          isHighlighted={!!highlightedIds.length}
          height="32px"
          background="legend"
          round="container2Round"
          align="center"
          pad={{ horizontal: 'spacing16' }}
          margin={{ bottom: 'spacing8' }}
          direction="row"
        >
          <Box width="400px">
            <Inscription color="textBody" weight="bold" lineHeight="18px" size="small">
              Name
            </Inscription>
          </Box>
          <Box width="350px">
            <Inscription color="textBody" weight="bold" lineHeight="18px" size="small">
              Allocation percentage
            </Inscription>
          </Box>
          <Box>
            <Inscription color="textBody" weight="bold" lineHeight="18px" size="small">
              Relationship
            </Inscription>
          </Box>
        </HighlightedWrapper>
      )}

      {beneficiaries?.length ? (
        <HighlightedWrapper
          isHighlighted={!!highlightedIds.length}
          round="container2Round"
          background="canvas"
          pad={isMobile ? 'spacing16' : { vertical: 'spacing18', horizontal: 'spacing16' }}
          border={{ size: 'small', color: 'border2' }}
          data-testId={`BeneficiariesList_${beneficiaryType}`}
          direction="row"
          justify="between"
          align="center"
        >
          <StyledWrapper flex>
            {beneficiaries?.map((beneficiary) =>
              isMobile ? (
                <Box
                  key={beneficiary?.id}
                  data-testId={`BeneficiariesList_item_${beneficiary?.id}`}
                  direction="row"
                  align="center"
                  justify="between"
                  gap="spacing12"
                >
                  <Box flex>
                    {beneficiary?.relationshipType ? (
                      <Box
                        data-testId="beneficiary_relationshipType"
                        alignSelf="start"
                        background="legend"
                        round="checkboxRound"
                        pad={{ horizontal: 'spacing8', vertical: 'spacing2' }}
                        margin={{ bottom: 'spacing4' }}
                      >
                        <Inscription color="textBody" size="12px" weight={500} lineHeight="18px">
                          {capitalize(formatSnakeCaseToCamelCase(beneficiary?.relationshipType as string))}
                        </Inscription>
                      </Box>
                    ) : null}
                    <Inscription
                      data-testId="beneficiary_name"
                      color="textBody"
                      size="16px"
                      weight="bold"
                      lineHeight="22px"
                    >
                      {beneficiary?.name}
                    </Inscription>
                  </Box>
                  <Inscription
                    data-testId="beneficiary_allocationPercentage"
                    color="textBody"
                    size="16px"
                    weight="bold"
                    lineHeight="22px"
                  >
                    {beneficiary?.allocationPercentage}%
                  </Inscription>
                </Box>
              ) : (
                <Box
                  key={beneficiary?.id}
                  data-testId={`BeneficiariesList_item_${beneficiary?.id}`}
                  direction="row"
                >
                  <Box data-testId="beneficiary_name" width={{ min: '400px' }} pad={{ right: 'spacing8' }}>
                    <Inscription color="textBody" size="16px" weight="bold" lineHeight="22px">
                      {beneficiary?.name}
                    </Inscription>
                  </Box>
                  <Box
                    data-testId="beneficiary_allocationPercentage"
                    width={{ min: '350px' }}
                    pad={{ right: 'spacing8' }}
                  >
                    <Inscription color="textBody" size="16px" lineHeight="22px">
                      {beneficiary?.allocationPercentage}%
                    </Inscription>
                  </Box>
                  <Box data-testId="beneficiary_relationshipType" pad={{ right: 'spacing8' }}>
                    <Inscription color="textBody" size="16px" lineHeight="22px">
                      {capitalize(formatSnakeCaseToCamelCase(beneficiary?.relationshipType as string))}
                    </Inscription>
                  </Box>
                </Box>
              )
            )}
          </StyledWrapper>
          <Box margin={{ left: 'spacing12' }}>
            <List.Item.Options data-testId="BeneficiariesList_edit_remove_buttons">
              <List.Item.Options.Item
                data-testId="BeneficiariesList_edit_button"
                onClick={() => setIsModalShown(true)}
              >
                Edit
              </List.Item.Options.Item>

              {isRemovingAllPrimaryBeneficiariesButtonHidden ? null : (
                <List.Item.Options.Item
                  data-testId="BeneficiariesList_remove_button"
                  onClick={() => setIsRemovingModalShown(true)}
                  isDanger
                  color="danger"
                >
                  Remove
                </List.Item.Options.Item>
              )}
            </List.Item.Options>
          </Box>
        </HighlightedWrapper>
      ) : (
        <Box
          justify="center"
          align="center"
          pad={isMobile ? {} : { top: 'spacing24' }}
          data-testId={`no_${beneficiaryType}_beneficiaries`}
        >
          <NoUsersIcon />
          <Box margin={{ top: 'spacing16' }}>
            <Inscription weight="bold" lineHeight="20px" color="textTitle">
              There are no beneficiaries added yet
            </Inscription>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BeneficiariesList;
